import React, { useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { FloatingPlacement, FloatingStateContext, PanelId, PanelRole, usePanelRegistry } from './panel-registry'
import { PanelChrome } from './panel-chrome'
import { OpenInNewWindowIcon } from '@radix-ui/react-icons'
import { Rnd } from 'react-rnd'
import { TooltipPortalContainer } from '@/components/ui-primitives/simple-tooltip'

export function PanelSlot(props: {
  id: PanelId
  role: PanelRole | "any"
  floating?: boolean
}) {
  return (
    <PanelSlotImpl
      id={props.id}
      role={props.role}
      floating={props.floating ?? false} />
  )
}

export function PanelSlots(props: {
  role: PanelRole | "any"
  floating?: boolean
  children?: (panel: React.ReactNode) => React.ReactNode
}) {
  const definitions = usePanelRegistry(r => r.definitions)
  const floatingPanels = usePanelRegistry(r => r.floatingPanels)
  const idsToRender = useMemo(() => {
    return Object.keys(definitions)
      .filter(id => {
        return props.role === "any" || definitions[id].role === props.role
      })
      .filter(id => {
        return props.floating === undefined || floatingPanels.includes(id)
      })
      .filter(id => {
        if (props.floating) return true
        if (definitions[id].role === "auxiliary" && floatingPanels.includes(id)) {
          return false
        } else {
          return true
        }
      })
  }, [ definitions, floatingPanels, props.role, props.floating ])

  return (
    <>
      {[ ...idsToRender ].map(panelId => (
        <PanelSlotImpl
          key={panelId}
          id={panelId}
          {...props} />
      ))}
    </>
  )
}

function PanelSlotImpl(props: {
  id: PanelId
  role: PanelRole | "any"
  floating?: boolean
}) {
  const rndRef = useRef<Rnd>(null)
  const definition = usePanelRegistry(r => r.definitions[props.id])
  const isFloating = usePanelRegistry(r => r.floatingPanels.includes(props.id))
  const render = usePanelRegistry(r => r.renderers[props.id])
  const renderHeaderExtraIcons = usePanelRegistry(r => r.headerExtraIconRenderers[props.id])
  const closeHandler = usePanelRegistry(r => r.closeHandlers[props.id])
  const shouldRender = usePanelRegistry(r => {
    const isRegistered = props.id in r.definitions && props.id in r.renderers
    const roleMatches = props.role === "any" || r.definitions[props.id]?.role === props.role
    // A floating slot only renders panels that are currently floating,
    // a docked slot only renders the currently open panel in the dock
    // happy-todo: name these states better purhaps
    const isVisibleInThisSlot = props.floating
      ? r.floatingPanels.includes(props.id)
      : r.openPanels.has(props.id)

    return isRegistered && roleMatches && isVisibleInThisSlot
  })

  const placement = usePanelRegistry(r => r.floatingPlacements[props.id])
  const zIndexOffset = usePanelRegistry(r => r.floatingPanels.indexOf(props.id))
  const isInFront = usePanelRegistry(r =>
    r.floatingPanels.indexOf(props.id) === r.floatingPanels.length - 1
  )

  const close = () => usePanelRegistry.getState().closePanel(props.id)
  const float = () => {
    usePanelRegistry.getState().closePanel(props.id)
    usePanelRegistry.getState().floatPanel(props.id)
  }
  const dock = () => usePanelRegistry.getState().dockPanel(props.id)

  const handleClose = () => {
    props.floating ? dock() : close()
    closeHandler?.()
  }

  const bringToFront = () => {
    usePanelRegistry.getState().bringToFront(props.id)
  }

  const availableActions = {
    float,
    dock,
    close: handleClose
  }

  if (!shouldRender) return null

  const chrome = (
    <FloatingStateContext.Provider value={
      props.floating
        ? (isInFront ? "floating-active" : "floating-inactive")
        : "docked"
    }>
      <PanelChrome>
        <PanelChrome.Header
          title={definition.title}
          icon={definition.icon}
          onClose={handleClose}
          showClose={definition.showCloseButton ?? true}
          extraIcons={(
            <>
              {renderHeaderExtraIcons?.(availableActions)}
              {!props.floating && (
                <PanelChrome.HeaderIcon
                  icon={OpenInNewWindowIcon}
                  title="Open in new window"
                  onClick={float}
                />
              )}
            </>
          )}
        >
          {definition.title}
        </PanelChrome.Header>
        <PanelChrome.Body>
          {(!props.floating && isFloating) ? (
            <div className="self-center text-gray-700 text-center m-4">
              This panel is currently floating<br />
              <button type="button" className="underline p-1" onClick={dock}>
                Move it back here
              </button>
            </div>
          ) : (
            <ErrorBoundary>{render(availableActions)}</ErrorBoundary>
          )}
        </PanelChrome.Body>
      </PanelChrome>
    </FloatingStateContext.Provider>
  )

  return props.floating
    ? (
       createPortal((
            <Rnd key={props.id}
                 ref={rndRef}
                 style={{ zIndex: 2100 + zIndexOffset }}
                 bounds="window"
                 dragHandleClassName="drag-handle"
                 onMouseDown={bringToFront}
                 onDragStart={bringToFront}
                 onResizeStart={bringToFront}
                 default={fitInWindow(placement ?? defaultFloatingPlacement(
                   definition.defaultFloatingDimensions?.width ?? 400,
                   definition.defaultFloatingDimensions?.height ?? 400,
                 ))}>
              <TooltipPortalContainer container={rndRef.current?.resizableElement.current ?? undefined}>
                {chrome}
              </TooltipPortalContainer>
            </Rnd>
          ),
          document.body,
          `floating-panel-${props.id}`
        )
    )
    : chrome
}

/** Clear of the window edges, which the resize handles stick out over */
const WINDOW_MARGIN = 16

/** Inside the browser window, as a panel sticking out makes the page scroll and shift */
function fitInWindow(placement: FloatingPlacement): FloatingPlacement {
  const width = Math.min(placement.width, window.innerWidth - 2 * WINDOW_MARGIN)
  const height = Math.min(placement.height, window.innerHeight - 2 * WINDOW_MARGIN)
  return {
    x: Math.min(Math.max(WINDOW_MARGIN, placement.x), window.innerWidth - width - WINDOW_MARGIN),
    y: Math.min(Math.max(WINDOW_MARGIN, placement.y), window.innerHeight - height - WINDOW_MARGIN),
    width,
    height,
  }
}

/** Top right of the app area, so left of the docked panel and side rail */
function defaultFloatingPlacement(width: number, height: number): FloatingPlacement {
  const app = document.getElementById("wistor-app-root")?.getBoundingClientRect()
  const right = app?.right ?? window.innerWidth
  const top = (app?.top ?? 0) + 16
  return {
    x: Math.max(0, right - width - 16),
    y: top,
    width,
    height: Math.min(height, window.innerHeight - top - 16),
  }
}

// tODO test this
class ErrorBoundary extends React.Component<{
  children: React.ReactNode
}, {
  error: Error | undefined
}> {
  state: { error: Error | undefined } = { error: undefined }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('Panel encountered an error', error, errorInfo)
  }

  render(): React.ReactNode {
    if (this.state.error) {
      return (
        <div role="alert" className="p-3 bg-red-100 text-xs">
          <h2 className="text-red-800 font-bold mb-1.5">Something went wrong:</h2>
          <pre className="whitespace-pre-wrap">
            {this.state.error?.message ?? 'Unknown error'}
          </pre>
        </div>
      )
    }

    return this.props.children
  }
}
